import React, { useEffect, useState } from 'react';
import {
  BrandMark,
  PinIcon,
  EyeIcon,
  EyeOffIcon,
  MinusIcon,
  ContrastIcon,
} from './icons';

const PIN_KEY     = 'terview.electron.alwaysOnTop';
const PRIVACY_KEY = 'terview.electron.contentProtection';
const OPACITY_KEY = 'terview.electron.opacity';
// Three-state cycle: 0 = solid, 1 = translucent (~70%), 2 = see-through (~40%).
// The button cycles forward each click; the body card opacity follows
// via the `body.translucent` / `body.see-through` classes (see
// ElectronShell.css). The topbar itself stays fully opaque so the user
// can always find the button to cycle back.
const OPACITY_LEVELS = [
  { cls: '',             label: 'Make window translucent — click to see screen behind' },
  { cls: 'translucent',  label: 'Translucent (70%) — click again for see-through' },
  { cls: 'see-through',  label: 'See-through (40%) — click to restore full opacity' },
];

/**
 * TopBar — slim window-chrome bar that lives across the top of the
 * Electron shell. Mirrors the LockedIn AI pattern:
 *   • brand mark + wordmark + tagline on the left
 *   • quick-action toggles on the right (always-on-top, privacy, hide)
 *
 * The whole bar is set as a drag region (-webkit-app-region: drag) so the
 * user can move the frameless-titlebar window by grabbing it. Action
 * buttons opt out of the drag region with -webkit-app-region: no-drag.
 *
 * macOS traffic lights live at trafficLightPosition (set in main.js); we
 * leave 78px of left padding so the bar doesn't overlap them.
 */
export default function TopBar() {
  // Always-on-top — useful when the user keeps Terview floating during a
  // live video interview. Persisted across launches.
  const [pinned, setPinned] = useState(() => {
    try { return localStorage.getItem(PIN_KEY) === '1'; } catch { return true; }
  });
  // Content protection — if true, screen-share / screenshot tools see a
  // black box where the window is. Critical for interview-copilot use.
  const [privacy, setPrivacy] = useState(() => {
    try { return localStorage.getItem(PRIVACY_KEY) === '1'; } catch { return false; }
  });
  // Transparency level — three-state cycle to fade the body card so the
  // desktop / video call behind shows through. Persisted across launches.
  const [opacityLevel, setOpacityLevel] = useState(() => {
    try {
      const raw = parseInt(localStorage.getItem(OPACITY_KEY) || '0', 10);
      return Number.isFinite(raw) && raw >= 0 && raw < OPACITY_LEVELS.length ? raw : 0;
    } catch { return 0; }
  });

  // Push the renderer state to the main process whenever it changes.
  // (Default at boot: alwaysOnTop=true is set by BrowserWindow constructor,
  // so we sync to whatever localStorage said the user wanted last time.)
  useEffect(() => {
    if (window.electron?.setAlwaysOnTop) window.electron.setAlwaysOnTop(pinned);
    try { localStorage.setItem(PIN_KEY, pinned ? '1' : '0'); } catch { /* ignore */ }
  }, [pinned]);

  useEffect(() => {
    if (window.electron?.setContentProtection) window.electron.setContentProtection(privacy);
    try { localStorage.setItem(PRIVACY_KEY, privacy ? '1' : '0'); } catch { /* ignore */ }
  }, [privacy]);

  // Toggle the body class that applies the transparency. We only ever
  // attach the active level's class, and remove the others, so cycling
  // is idempotent. Cleanup on unmount removes any class we set so the
  // website (with .electron-app removed) doesn't inherit it.
  useEffect(() => {
    OPACITY_LEVELS.forEach(({ cls }) => {
      if (!cls) return;
      document.body.classList.remove(cls);
    });
    const active = OPACITY_LEVELS[opacityLevel]?.cls;
    if (active) document.body.classList.add(active);
    try { localStorage.setItem(OPACITY_KEY, String(opacityLevel)); } catch { /* ignore */ }
    return () => {
      OPACITY_LEVELS.forEach(({ cls }) => {
        if (!cls) return;
        document.body.classList.remove(cls);
      });
    };
  }, [opacityLevel]);

  const cycleOpacity = () => setOpacityLevel(l => (l + 1) % OPACITY_LEVELS.length);

  const minimize = () => { window.electron?.minimize?.(); };

  return (
    // Intentionally a <div role="banner"> rather than <header> — the
    // website's NeuralGlassHomepage.css ships a global `header { … }`
    // rule (position: fixed, max-width: 1200px, backdrop-filter blur,
    // border-radius: 25px) meant for the marketing nav. Using a <div>
    // means that global selector never matches our floating topbar.
    <div role="banner" className="es-topbar">
      <div className="es-topbar-brand">
        <BrandMark size={30} />
        <div className="es-topbar-brand-meta">
          <div className="es-topbar-name">
            Ter<span className="es-topbar-name-accent">view</span>
          </div>
          <div className="es-topbar-tagline">AI CAREER COPILOT</div>
        </div>
      </div>

      <div className="es-topbar-spacer" />

      <div className="es-topbar-actions">
        <ActionButton
          active={pinned}
          onClick={() => setPinned(p => !p)}
          title={pinned ? 'Pinned: always on top — click to unpin' : 'Pin window — keep it above other apps'}
          aria-label="Toggle always on top"
        >
          <PinIcon size={18} />
        </ActionButton>

        <ActionButton
          active={privacy}
          onClick={() => setPrivacy(p => !p)}
          title={privacy
            ? 'Privacy mode ON — screen-share & screenshots will not capture this window'
            : 'Privacy mode OFF — click to hide Terview from screen-shares'}
          aria-label="Toggle privacy mode"
          variant={privacy ? 'success' : undefined}
        >
          {privacy ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
        </ActionButton>

        <ActionButton
          active={opacityLevel > 0}
          onClick={cycleOpacity}
          title={OPACITY_LEVELS[opacityLevel]?.label}
          aria-label="Cycle window transparency"
        >
          <ContrastIcon size={18} />
        </ActionButton>

        <ActionButton
          onClick={minimize}
          title="Minimize window"
          aria-label="Minimize"
        >
          <MinusIcon size={18} />
        </ActionButton>
      </div>
    </div>
  );
}

function ActionButton({ children, active, variant, ...rest }) {
  const cls = [
    'es-topbar-btn',
    active ? 'active' : '',
    variant ? `variant-${variant}` : '',
  ].filter(Boolean).join(' ');
  return (
    <button type="button" className={cls} {...rest}>
      {children}
    </button>
  );
}
